import React from 'react';
import Box from '@mui/material/Box';
import { IUserMenuItems } from '@components/UserMenu/UserMenu';
import {
  Drawer,
  Grid2,
  List,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { IMainNavLinkItems } from '../../constants/pages';
import { useTranslation } from 'react-i18next';
import { RootState } from '@redux/store';
import { useSelector } from 'react-redux';
import { UserState } from '@features/Auth/user.slice';
import { css } from '@emotion/css';

interface NavBarDrawerProps {
  drawerOpen: boolean;
  toggleDrawer: (event: React.KeyboardEvent | React.MouseEvent) => void;
  truncateUsername: () => string;
  isActive: (path: string) => boolean;
  pages: IMainNavLinkItems[];
  userMenuItems: IUserMenuItems[];
  tryLogout: () => void;
  accountName: string;
}

const NavBarDrawer: React.FC<NavBarDrawerProps> = ({
  drawerOpen,
  toggleDrawer,
  truncateUsername,
  isActive,
  pages,
  userMenuItems,
  tryLogout,
  accountName,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const userState: UserState = useSelector((state: RootState) => state.user);

  const styles = {
    LinkItem: css`
        border-right: none !important;
        text-decoration: none;
        color: ${theme.palette.secondary.main};
        height: 100% !important;
        width: 100% !important;
        padding: 16px 14px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        text-align: left !important;

        &:hover {
            background-color: transparent !important;
        }
    `,
    active: css`
      color: ${theme.palette.secondary.main} !important;
    `,
    navHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      pt: '.8rem',
      px: '16px',
      closeIcon: {
        color: theme.palette.secondary.main,
        ':hover': { cursor: 'pointer' },
      },
    },
    accountInfo: {
      container: {
        flexDirection: 'column !important',
        justifyContent: 'center',
        margin: '0.5rem 1.2rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '.5rem',
      },
      text: {
        display: 'block',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 'inherit !important',
        lineHeight: 'inherit !important',
        margin: '-0.2rem !important',
      },
    },
    navPages: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      pl: '16px',
      ':hover': {
        backgroundColor: theme.palette.accent.main,
      },
    },
    userMenu: {
      header: {
        ml: '16px',
        mb: '10px',
        fontWeight: 'bold',
        color: theme.palette.secondary.dark,
        fontSize: theme.customValues.fontSizes.body14,
      },
      items: {
        fontSize: theme.customValues.fontSizes.body14,
        mb: '0px',
      },
    },
  };

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={toggleDrawer}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.offwhite.main,
        },
      }}
    >
      <Box width={250} role="presentation">
        <Box sx={styles.navHeader}>
          <Typography variant="body2" fontWeight="bold">
            {t('signed_in')}
          </Typography>
          <CloseIcon
            sx={styles.navHeader.closeIcon}
            onClick={(e) => toggleDrawer(e)}
            onKeyDown={(e) => toggleDrawer(e)}
          />
        </Box>
        <Grid2 sx={styles.accountInfo.container} container>
          <Typography sx={styles.accountInfo.text}>
            {truncateUsername()}
          </Typography>
          <Typography sx={styles.accountInfo.text}>{accountName}</Typography>
        </Grid2>
        <List>
          {pages.map((item: IMainNavLinkItems, i: number) =>
            !item.roleRequired ||
            userState?.user?.roles?.includes(item?.roleRequired) ? (
              <Box
                key={i}
                sx={{
                  backgroundColor:
                    isActive(item.location) && theme.palette.accent.main,
                  ...styles.navPages,
                }}
              >
                {item.listIcon &&
                  React.cloneElement(item.listIcon, {
                    sx: {
                      color: isActive(item.location)
                        ? theme.palette.secondary.main
                        : theme.palette.secondary.main,
                    },
                  })}
                <Link
                  key={item.name}
                  to={item.location}
                  className={`${styles.LinkItem} ${
                    isActive(item.location) && styles.active
                  }`}
                >
                  <span>{item.name}</span>
                </Link>
              </Box>
            ) : null
          )}
          <Box mt="1rem">
            <Typography variant="body2" sx={styles.userMenu.header}>
              {t('more')}
            </Typography>
            {userMenuItems.map((item: IUserMenuItems) => (
            !item.roleRequired ||
            userState?.user?.roles?.includes(item?.roleRequired) ? 
              <MenuItem
                component={Link}
                to={item.link}
                onClick={(
                  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                ) => {
                  if (item.onClick) {
                    item.onClick(event);
                  }
                }}
                key={item.id}
                sx={styles.userMenu.items}
              >
                {item.message}
              </MenuItem> : null
            ))}
            <MenuItem
              onClick={tryLogout}
              key="logout"
              sx={{ fontSize: theme.customValues.fontSizes.body14 }}
            >
              {t('sign_out')}
            </MenuItem>
          </Box>
        </List>
      </Box>
    </Drawer>
  );
};

export default NavBarDrawer;
